// "Quanto eu vou receber no mês de setembro/2026?" — a pergunta do CEO.
//
// Soma a previsão real por UC (`agregarPorVencimento`), a mesma função que a
// tela operacional `views/Financeiro/PrevisaoRecebiveisView.tsx` usa, através
// do mesmo hook. Duas buscas separadas divergiriam em silêncio e a diretoria
// discutiria com o operacional qual tela está certa.
//
// Três números diferentes, deliberadamente separados:
//
//   A FATURAR = já emitido (fato) + previsto de UC que comprovadamente fatura
//   A RECEBER = a faturar × a taxa histórica de recebimento, medida
//   EM ENTRADA = UC nova, que ainda não faturou — fora do total
//
// O último existe porque em 29/08/2026 havia R$ 291 mil de clientes em 1º ciclo
// e sem injeção projetados para setembro. Somá-los responderia a pergunta
// errada: é carteira futura, não caixa do mês.

import React, { useMemo, useState } from 'react';
import { AlertTriangle, TrendingUp } from 'lucide-react';
import {
  agregarPorVencimento,
  calcularCoberturaDoMes,
  calcularRecebimentoDoMes,
  rotuloDeOrdinal,
} from '../../../../../src/lib/recebiveisUc';
import type { AgendaDaUc, TaxaRecebimento } from '../../../../../src/lib/recebiveisUc';
import InfoTip from '../../../../../src/components/InfoTip';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';

/** Balde zerado, para a conta não precisar de caminho especial quando não há mês. */
const VAZIO = { valorBoleto: 0, valorVigor: 0, ucs: 0 };

const fmtBRL = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 0, maximumFractionDigits: 0 });

interface Props {
  agendas: AgendaDaUc[];
  taxaRecebimento: TaxaRecebimento;
  loading: boolean;
  erro: string | null;
}

const RecebimentoDoMes: React.FC<Props> = ({ agendas, taxaRecebimento, loading, erro }) => {
  const totais = useMemo(() => agregarPorVencimento(agendas), [agendas]);

  // Começa no PRÓXIMO mês: o mês corrente já está quase todo vencido, e a
  // pergunta do CEO é sobre o que vem.
  const proximo = useMemo(() => {
    const hoje = new Date();
    return hoje.getFullYear() * 12 + hoje.getMonth() + 1;
  }, []);
  const [ordinal, setOrdinal] = useState<number | null>(null);
  const alvo = ordinal ?? proximo;
  const mes = totais.find((t) => t.ordinal === alvo);

  // LoadingSpinner, não texto solto: toda outra seção do Painel Executivo
  // (Financeiro, Comercial, Marketing, Geradoras) usa este mesmo componente
  // para carregar — um parágrafo cinza aqui destoaria bem no meio das outras
  // seções já carregadas.
  if (loading) return <Moldura><LoadingSpinner label="Somando a previsão de todas as UCs..." /></Moldura>;
  if (erro) return <Moldura><p className="text-sm text-red-600 dark:text-red-400">{erro}</p></Moldura>;

  // A conta vive em `calcularRecebimentoDoMes`, não aqui: é a única linha do
  // sistema que produz o número da diretoria. Ela soma `valorVigor` (o boleto
  // único da Gran entra sem a Equatorial e só com a parte da Vigor), e as
  // caixas abaixo têm de usar o MESMO campo para somarem o "a faturar" — o
  // projeto não tem teste de componente que pegasse a troca.
  const mesOuVazio = mes ?? { emitido: VAZIO, projetado: VAZIO, emEntrada: VAZIO };
  const { aFaturar, aReceber, inadimplencia } = calcularRecebimentoDoMes(mesOuVazio, taxaRecebimento);
  const cobertura = calcularCoberturaDoMes(mesOuVazio, totais);

  return (
    <Moldura>
      {/* As pílulas ficam FORA do `if (!mes)` de propósito: se o mês
          selecionado por padrão (o próximo) não tiver previsão, o usuário
          precisa continuar podendo tentar outro mês — não ficar preso numa
          mensagem de "sem previsão" sem seletor nenhum na tela. */}
      {/* TODOS os meses que a agenda conhece, não um recorte fixo. Os mais
          distantes existem porque já têm boleto emitido (parcelamentos vão até
          meados de 2027) — são compromisso firme e o CEO deve poder olhá-los.
          `só emitido` marca os que não têm previsão nova, só o que já foi
          cobrado, para o número pequeno não parecer queda de faturamento. */}
      {totais.length > 0 && (
        <div className="mb-4 flex w-fit flex-wrap items-center gap-1 rounded-full bg-gray-100 p-1 dark:bg-gray-900/40">
          {totais.map((t) => {
            const parcial = !calcularCoberturaDoMes(t, totais).completa;
            return (
              <button
                key={t.ordinal}
                type="button"
                onClick={() => setOrdinal(t.ordinal)}
                aria-pressed={t.ordinal === alvo}
                title={parcial ? `${t.rotulo}: cobertura parcial, na ponta do horizonte` : t.rotulo}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                  t.ordinal === alvo
                    ? 'bg-primary text-white'
                    : 'text-gray-500 hover:bg-gray-200 dark:text-gray-400 dark:hover:bg-gray-700'
                }`}
              >
                {t.rotulo}
                {parcial && <span className="ml-1 opacity-60" aria-hidden>·</span>}
              </button>
            );
          })}
        </div>
      )}

      {!mes && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Sem previsão para {rotuloDeOrdinal(alvo)}. Isso acontece quando as UCs ainda não têm
          faturas suficientes para estimar consumo, tarifa e vencimento — tente outro mês acima.
        </p>
      )}

      {mes && !cobertura.completa && (
        <p className="mb-3 rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800 dark:bg-sky-900/25 dark:text-sky-300">
          {rotuloDeOrdinal(alvo)} está na ponta do horizonte: o número abaixo cobre{' '}
          <strong>{cobertura.ucs} de ~{cobertura.ucsTipico} UCs</strong> — é o que já se enxerga
          (boletos emitidos e as primeiras previsões), não uma queda de faturamento.
        </p>
      )}

      {mes && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {/* O número que responde a pergunta. */}
            {/* justify-center: o destaque estica para acompanhar as duas caixas
                empilhadas à direita; sem isto sobra um vazio grande embaixo do
                número em vez de respiro simétrico em volta dele. */}
            <div className="flex flex-col justify-center rounded-xl border-2 border-primary/30 bg-primary/5 p-4 sm:col-span-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                A receber em {rotuloDeOrdinal(alvo)}
              </p>
              <p className="mt-1 text-3xl font-black tabular-nums text-gray-900 dark:text-white">
                {fmtBRL(aReceber)}
              </p>
              <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                {fmtBRL(aFaturar)} a faturar, menos {(inadimplencia * 100).toFixed(1)}% que historicamente não entra
                <InfoTip label="De onde vem a taxa de recebimento" tone={taxaRecebimento.medida ? 'default' : 'warning'}>
                  {taxaRecebimento.medida ? (
                    <p>
                      Medida no próprio histórico: {taxaRecebimento.meses} meses já maduros,
                      {' '}{fmtBRL(taxaRecebimento.emitido)} emitidos e {fmtBRL(taxaRecebimento.recebido)} recebidos.
                      Meses recentes ficam de fora porque ainda estão sendo pagos. Boletos cancelados e
                      faturas parceladas também: a dívida parcelada é contada pelas parcelas.
                    </p>
                  ) : (
                    <p>Não há histórico maduro suficiente — usando a média da carteira.</p>
                  )}
                </InfoTip>
              </p>
            </div>

            <div className="grid gap-3">
              {/* Sem isto, "Já emitido" + "Previsto" fica parecendo compor o
                  destaque "A receber" ao lado — que já vem líquido de
                  inadimplência — em vez do "a faturar" citado na legenda dele. */}
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
                Somam "a faturar"
              </p>
              <div className="rounded-xl border border-gray-100 p-3 dark:border-gray-700">
                <p className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">Já emitido</p>
                <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                  {fmtBRL(mes.emitido.valorVigor)}
                </p>
                <p className="text-[10px] text-gray-400">{mes.emitido.ucs} boletos existem</p>
              </div>
              <div className="rounded-xl border border-gray-100 p-3 dark:border-gray-700">
                <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-400">Previsto</p>
                <p className="text-lg font-bold tabular-nums text-gray-900 dark:text-white">
                  {fmtBRL(mes.projetado.valorVigor)}
                </p>
                <p className="text-[10px] text-gray-400">
                  {mes.projetado.ucs} UCs
                  {mes.ucsBaixaConfianca > 0 && (
                    <span className="text-orange-600 dark:text-orange-400">
                      {' '}· {mes.ucsBaixaConfianca} {mes.ucsBaixaConfianca === 1 ? 'frágil' : 'frágeis'}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {mes.emEntrada.valorVigor > 0 && (
            <div className="mt-3 flex items-start gap-2 rounded-xl bg-gray-50 p-3 text-xs text-gray-600 dark:bg-gray-900/40 dark:text-gray-400">
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-gray-400" />
              <p>
                <strong className="text-gray-800 dark:text-gray-200">
                  {fmtBRL(mes.emEntrada.valorVigor)}
                </strong>{' '}
                de {mes.emEntrada.ucs} UCs em entrada não entram em nenhum dos números acima — são clientes em 1º ciclo
                ou ainda sem injeção, que nunca foram cobrados. É carteira futura, não caixa deste mês.
              </p>
            </div>
          )}
        </>
      )}
    </Moldura>
  );
};

const Moldura: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
    <h2 className="flex items-center gap-2 text-sm font-bold text-gray-800 dark:text-white">
      <TrendingUp size={16} className="text-primary" />
      Quanto vou receber
    </h2>
    <p className="mb-4 mt-1 text-xs italic text-gray-500 dark:text-gray-400">
      Soma da previsão de cada UC, por mês de vencimento — não uma estimativa de carteira.
    </p>
    {/* Dito por extenso: este card tem o próprio seletor de mês e NÃO segue o
        período da barra de filtros. Sem o aviso, ver "SET/2026" aqui com outro
        mês filtrado acima parece o mesmo defeito que os gráficos tinham. */}
    <p className="mb-4 -mt-3 text-[11px] text-gray-400 dark:text-gray-500">
      Escolha o mês nas pílulas abaixo — este card não segue o período filtrado no topo da tela.
    </p>
    {children}
  </div>
);

export default RecebimentoDoMes;
